type BookingFormState = {
  branch: any | null;
  barber: any | null;
  date: any | null;
  service: any[] | null;
  client_name: string | null;
  client_tel: string | null;
  client_mail: string | null;
  client_comment: string | null;
  client_notif: string | null;
  client_agreement: boolean;
};

export type VoucherProductSummary = {
  id: string;
  title: string;
  price: number;
  currency: string;
};

export type VoucherCheckoutSnapshot = {
  order_id: string;
  total: number;
  currency: string;
  delivery_method: "digital" | "pickup" | "mail" | "parcelshop";
  items: {
    product_id: string;
    title: string;
    quantity: number;
    price: number;
  }[];
};

export interface TrackEvents {
  "phone-clicked": [branch: "zizkov" | "nusle"];
  "booking-open-review": [bookingFormState: BookingFormState];
  "booking-finished": [bookingFormState: BookingFormState];
  "booking-services-selected": [bookingFormState: BookingFormState];
  "booking-select-content": [type: "branch" | "barber", id: string];
  "booking-form": [
    type: "success" | "failure" | "progress" | "regression",
    tabName: string
  ];
  "voucher-services-loaded": [products: VoucherProductSummary[]];
  "voucher-purchase-initiated": [snapshot: VoucherCheckoutSnapshot];
  "voucher-purchase-completed": [snapshot: VoucherCheckoutSnapshot];
}

// type TrackArgs =
//   | phoneClicked
//   | bookingOpenReview
//   | bookingFinished
//   | bookingServicesSelected;

export interface Track {
  <Event extends keyof TrackEvents>(
    command: Event,
    ...args: TrackEvents[Event]
  ): void;
}

export const useTrack: Track = (event, ...args) => {
  const { $fbq } = useNuxtApp();
  const { gtag } = useGtag();

  /**
   * "phone-clicked"
   */
  if (event === "phone-clicked") {
    $fbq("track", "Contact", { type: "phone", branch: args[0] });
    return;
  }

  /**
   * "booking-open-review"
   */
  if (event === "booking-open-review") {
    $fbq("track", "InitiateCheckout", {
      currency: "CZK",
      value: getFormStateValue(args[0] as BookingFormState),
      content_ids: getFormStateItemIds(args[0] as BookingFormState),
      content_category: getFormStateCategory(args[0] as BookingFormState),
      num_items: getFormStateItemsLength(args[0] as BookingFormState),
    });
    gtag("event", "begin_checkout", {
      currency: "CZK",
      value: getFormStateValue(args[0] as BookingFormState),
      items: getFormStateGtagItems(args[0] as BookingFormState),
    });
    return;
  }

  /**
   * "booking-finished"
   */
  if (event === "booking-finished") {
    $fbq("track", "Lead", {
      currency: "CZK",
      value: getFormStateValue(args[0] as BookingFormState),
      content_category: getFormStateCategory(args[0] as BookingFormState),
    });
    gtag("event", "generate_lead", {
      currency: "CZK",
      value: getFormStateValue(args[0] as BookingFormState),
    });
    return;
  }

  /**
   * "booking-services-selected"
   */
  if (event === "booking-services-selected") {
    $fbq("track", "AddToCart", {
      currency: "CZK",
      value: getFormStateValue(args[0] as BookingFormState),
      content_ids: getFormStateItemIds(args[0] as BookingFormState),
    });
    gtag("event", "add_to_cart", {
      currency: "CZK",
      value: getFormStateValue(args[0] as BookingFormState),
      items: getFormStateGtagItems(args[0] as BookingFormState),
    });
    return;
  }

  /**
   * "booking-select-content"
   */
  if (event === "booking-select-content") {
    gtag("event", "select_content", {
      content_type: args[0],
      content_id: args[1],
    });
    return;
  }

  /**
   * "booking-form"
   */
  if (event === "booking-form") {
    gtag("event", "booking_form", {
      booking_form_interaction_status: args[0],
      booking_form_interaction_tab: args[1],
    });
    return;
  }

  /**
   * "voucher-services-loaded" — fired once the gift-card storefront's
   * products list has resolved. Mirrors a product-list impression.
   */
  if (event === "voucher-services-loaded") {
    const products = args[0] as VoucherProductSummary[];
    $fbq("track", "ViewContent", {
      content_type: "product_group",
      content_ids: products.map((p) => p.id),
      content_name: "Gift vouchers",
      content_category: "gift_voucher",
    });
    gtag("event", "view_item_list", {
      item_list_id: "gift_vouchers",
      item_list_name: "Gift vouchers",
      items: products.map((p, i) => ({
        item_id: p.id,
        item_name: p.title,
        item_category: "gift_voucher",
        price: p.price,
        currency: p.currency,
        index: i,
      })),
    });
    return;
  }

  /**
   * "voucher-purchase-initiated" — fired right before the customer is
   * redirected to Comgate. Carries the order_id created by our backend
   * plus the full cart so GA's begin_checkout has line items.
   */
  if (event === "voucher-purchase-initiated") {
    const s = args[0] as VoucherCheckoutSnapshot;
    $fbq("track", "InitiateCheckout", {
      currency: s.currency,
      value: s.total,
      content_ids: s.items.map((i) => i.product_id),
      content_category: "gift_voucher",
      num_items: s.items.reduce((acc, i) => acc + i.quantity, 0),
    });
    gtag("event", "begin_checkout", {
      currency: s.currency,
      value: s.total,
      items: s.items.map((i, idx) => ({
        item_id: i.product_id,
        item_name: i.title,
        item_category: "gift_voucher",
        price: i.price,
        quantity: i.quantity,
        currency: s.currency,
        index: idx,
      })),
    });
    return;
  }

  /**
   * "voucher-purchase-completed" — fired on /return once the polling
   * loop confirms a paid status. The snapshot is rehydrated from
   * sessionStorage (written in submit()) so the event has value/items
   * even though /status only returns id + status.
   */
  if (event === "voucher-purchase-completed") {
    const s = args[0] as VoucherCheckoutSnapshot;
    $fbq("track", "Purchase", {
      currency: s.currency,
      value: s.total,
      content_ids: s.items.map((i) => i.product_id),
      content_category: "gift_voucher",
      num_items: s.items.reduce((acc, i) => acc + i.quantity, 0),
    });
    gtag("event", "purchase", {
      transaction_id: s.order_id,
      currency: s.currency,
      value: s.total,
      items: s.items.map((i, idx) => ({
        item_id: i.product_id,
        item_name: i.title,
        item_category: "gift_voucher",
        price: i.price,
        quantity: i.quantity,
        currency: s.currency,
        index: idx,
      })),
    });
    return;
  }
};

function getFormStateValue(state: BookingFormState) {
  return +state?.service?.reduce((acc, i) => acc + i?.price_min, 0);
}
function getFormStateItemIds(state: BookingFormState) {
  return state?.service?.map((i) => i?.id);
}
function getFormStateCategory(state: BookingFormState) {
  return state?.branch?.attributes?.title;
}
function getFormStateItemsLength(state: BookingFormState) {
  return state?.service?.length;
}
function getFormStateGtagItems(state: BookingFormState) {
  return state?.service?.map((i) => {
    return {
      item_id: i.id,
      item_name: i.title,
      index: i.weight,
      price: i.price_min,
      currency: "CZK",
    };
  });
}
